import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Ship, Check, Loader2, FileDown, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTRACT_ARTICLES = [
  { num: 1, title: "Objet du contrat", summary: "Distribution des croisières catamaran par l'Agence" },
  { num: 2, title: "Obligations de l'Armateur", summary: "Navires conformes, équipage, assurances, disponibilités" },
  { num: 3, title: "Obligations de l'Agence", summary: "Promotion, information clients, paiements, respect des tarifs" },
  { num: 4, title: "Tarifs et Commission", summary: "Tarifs de base Armateur, marge libre de l'Agence, caisse de bord" },
  { num: 5, title: "Processus de réservation", summary: "Demande de dispo, blocage 3 jours, devis client, confirmation" },
  { num: 6, title: "Conditions de paiement", summary: "Échéancier selon délai avant départ (35/35/30 ou 50/50 ou 100%)" },
  { num: 7, title: "Annulations et modifications", summary: "Barème d'annulation client, remboursement si annulation Armateur" },
  { num: 8, title: "Conditions météorologiques, retards de transport et autorité du Capitaine", summary: "Le Capitaine est seul maître à bord. Aucun remboursement en cas de modification météo. Le Capitaine n'est pas responsable des retards d'avion ou de transport. Les passagers doivent être ponctuels à l'embarquement." },
  { num: 9, title: "Responsabilités et assurances", summary: "RC pro, assurance maritime, recommandation assurance voyage" },
  { num: 10, title: "Durée et résiliation", summary: "Tacite reconduction, préavis 3 mois, réservations maintenues" },
  { num: 11, title: "Confidentialité", summary: "Informations commerciales et tarifaires confidentielles (2 ans)" },
  { num: 12, title: "Loi applicable et juridiction", summary: "Droit français, Tribunaux de Paris" },
];

function formatDateTimeFr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ContractSignPage() {
  const { signToken } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [showArticles, setShowArticles] = useState(false);
  const [showSignPad, setShowSignPad] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/partnership-contract/public/${signToken}`)
      .then(r => {
        setContract(r.data);
        if (r.data.agency_signed_at) setSigned(true);
      })
      .catch(() => setError('Ce lien est invalide ou a expiré.'))
      .finally(() => setLoading(false));
  }, [signToken]);

  const startDraw = (e) => {
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0A2540';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleSign = async () => {
    if (!signerName.trim()) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let hasDrawing = false;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) { hasDrawing = true; break; }
    }
    if (!hasDrawing) return;

    setSigning(true);
    try {
      const sigData = canvas.toDataURL('image/png');
      await axios.post(`${API}/partnership-contract/public/${signToken}/sign`, {
        signature: sigData,
        signer_name: signerName.trim(),
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/partnership-contract/public/${signToken}/pdf`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#C4724F] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center">
        <Ship className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (!contract) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2540] to-[#0d3050]" data-testid="contract-sign-page">
      {/* Header */}
      <div className="bg-[#0A2540] border-b border-white/10 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Ship className="h-8 w-8 text-[#C4724F] mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-white">Contrat de Partenariat Commercial</h1>
          <p className="text-white/50 text-sm mt-1">SOGNUDIMARE — Croisières Catamaran</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Contract info card */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="contract-info-card">
          <div className="h-1.5 bg-gradient-to-r from-[#0A2540] via-[#0A2540] to-[#C4724F]" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-mono text-gray-400">{contract.reference}</span>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                signed || contract.status === 'active' ? 'bg-green-100 text-green-700' :
                contract.status === 'signed_armateur' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`} data-testid="contract-status">
                {signed || contract.status === 'active' ? 'Signé' :
                 contract.status === 'signed_armateur' ? 'En attente de votre signature' :
                 'En cours'}
              </span>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">L'Armateur</p>
                <p className="font-bold text-[#0A2540]">{contract.armateur?.name}</p>
                {contract.armateur?.address && <p className="text-sm text-gray-500">{contract.armateur.address}</p>}
                {contract.armateur?.siret && <p className="text-xs text-gray-400 mt-1">SIRET : {contract.armateur.siret}{contract.armateur?.ape ? ` — APE : ${contract.armateur.ape}` : ''}</p>}
                {contract.armateur?.representative && <p className="text-xs text-gray-500 mt-0.5">Représentant : {contract.armateur.representative}</p>}
                {contract.armateur_signed_at && (
                  <div className="flex items-center gap-1 mt-2">
                    <Check className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs text-green-700">Signé le {formatDateTimeFr(contract.armateur_signed_at)}</span>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#C4724F] uppercase mb-2">L'Agence</p>
                <p className="font-bold text-[#0A2540]">{contract.agency_info?.name || contract.agency_name}</p>
                {contract.agency_info?.address && <p className="text-sm text-gray-500">{contract.agency_info.address}</p>}
                {contract.agency_info?.siret && <p className="text-xs text-gray-400 mt-1">SIRET : {contract.agency_info.siret}{contract.agency_info?.ape ? ` — APE : ${contract.agency_info.ape}` : ''}</p>}
                {contract.agency_info?.licence && <p className="text-xs text-gray-400">Licence : {contract.agency_info.licence}</p>}
                {contract.agency_info?.representative && <p className="text-xs text-gray-500 mt-0.5">Représentante : {contract.agency_info.representative}</p>}
                {contract.agency_signed_at && (
                  <div className="flex items-center gap-1 mt-2">
                    <Check className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs text-green-700">Signé le {formatDateTimeFr(contract.agency_signed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 pb-4 border-b">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Durée : <strong>{contract.duration || '1 an'}</strong> (tacite reconduction)</span>
              </div>
            </div>

            {/* Articles summary */}
            <div>
              <button
                onClick={() => setShowArticles(!showArticles)}
                className="flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:text-[#C4724F] transition-colors w-full"
                data-testid="toggle-articles"
              >
                {showArticles ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Articles du contrat ({CONTRACT_ARTICLES.length} articles)
              </button>

              {showArticles && (
                <div className="mt-3 space-y-2" data-testid="articles-list">
                  {CONTRACT_ARTICLES.map(a => (
                    <div key={a.num} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <span className="w-6 h-6 rounded-full bg-[#0A2540] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                        {a.num}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                        <p className="text-xs text-gray-500">{a.summary}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Download PDF */}
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-2" data-testid="download-contract-pdf">
                <FileDown className="h-4 w-4" /> Télécharger le contrat complet (PDF)
              </Button>
            </div>
          </div>
        </div>

        {/* Signature section */}
        {!signed && contract.status !== 'active' ? (
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="signature-section">
            <div className="h-1.5 bg-[#C4724F]" />
            <div className="p-6">
              <h3 className="text-lg font-bold text-[#0A2540] mb-1">Signer le contrat</h3>
              <p className="text-sm text-gray-500 mb-4">
                En signant ce contrat, vous acceptez l'ensemble des conditions décrites dans les 12 articles ci-dessus.
              </p>

              {!showSignPad ? (
                <Button
                  onClick={() => setShowSignPad(true)}
                  className="bg-[#C4724F] hover:bg-[#b5634a] w-full sm:w-auto"
                  data-testid="begin-sign-btn"
                >
                  J'ai lu et j'accepte — Signer le contrat
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Nom complet du signataire</label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={e => setSignerName(e.target.value)}
                      placeholder="Prénom Nom"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#C4724F] focus:border-transparent outline-none"
                      data-testid="signer-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Votre signature</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={560}
                        height={180}
                        className="w-full touch-none cursor-crosshair"
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={endDraw}
                        data-testid="signature-canvas"
                      />
                    </div>
                    <button onClick={clearCanvas} className="text-xs text-gray-400 hover:text-red-500 mt-1" data-testid="clear-signature">
                      Effacer la signature
                    </button>
                  </div>

                  <Button
                    onClick={handleSign}
                    disabled={signing || !signerName.trim()}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 w-full"
                    data-testid="confirm-sign-btn"
                  >
                    {signing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Confirmer et signer
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="signed-confirmation">
            <div className="h-1.5 bg-green-500" />
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Contrat signé</h3>
              <p className="text-gray-500 mb-4">
                Le contrat de partenariat a été signé par les deux parties. Vous pouvez télécharger le PDF pour vos archives.
              </p>
              <Button variant="outline" onClick={downloadPdf} className="gap-2" data-testid="download-signed-pdf">
                <FileDown className="h-4 w-4" /> Télécharger le contrat signé
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-white/30 text-xs pb-8">
          SOGNUDIMARE SARL — Croisières en catamaran — Corse & Méditerranée
        </p>
      </div>
    </div>
  );
}
